import { makeStyles } from '@griffel/react';
import type { MeResponse } from '@ksb/shared';
import { Button, HeaderBar, HeaderBrandContent, MEDIA_MOBILE, SignOut, User } from '@ksb/ui';
import { Suspense } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router';
import { abmelden } from '../auth/abmelden';
import { Ladeanzeige } from './Ladeanzeige';

const useStyles = makeStyles({
  inhalt: {
    boxSizing: 'border-box',
    maxWidth: '960px',
    margin: '0 auto',
    padding: 'var(--padding-xl) clamp(16px, 3vw, 48px) var(--padding-3xl)',
    [MEDIA_MOBILE]: { paddingTop: 'var(--padding-lg)' },
  },
});

interface NavEintrag {
  ziel: string;
  text: string;
  sichtbar: boolean;
}

export function Layout({ benutzer }: { benutzer: MeResponse }) {
  const styles = useStyles();
  const istAdmin = benutzer.rollen.includes('Admin');
  // Die Verwaltung bringt ihren eigenen Rahmen mit (Seitenleiste, volle Breite) statt der schmalen Spalte.
  const verwaltung = useLocation().pathname.startsWith('/admin');

  const eintraege: NavEintrag[] = [
    { ziel: '/schulungen', text: 'Meine Schulungen', sichtbar: benutzer.istTeilnehmer },
    { ziel: '/buchungen', text: 'Meine Buchungen', sichtbar: benutzer.istTeilnehmer },
    { ziel: '/admin', text: 'Verwaltung', sichtbar: istAdmin },
  ];

  const sichtbareEintraege = eintraege.filter((e) => e.sichtbar);

  return (
    <>
      <HeaderBar
        breit={verwaltung}
        marke={
          <Link
            to="/"
            className="ksb-header__brand ksb-focusable"
            aria-label="Schulungsportal, zur Startseite"
          >
            <HeaderBrandContent />
          </Link>
        }
        mitte={<span className="ksb-header__name">Schulungsportal</span>}
        werkzeuge={
          <>
            <span className="ksb-header__user">
              <User aria-hidden="true" />
              {benutzer.name}
              {istAdmin && ' · Admin'}
            </span>
            <Button
              variant="text"
              size="sm"
              contrast="negative"
              onClick={() => void abmelden()}
              aria-label="Abmelden"
            >
              <SignOut size={20} aria-hidden="true" />
              <span className="ksb-header__user">Abmelden</span>
            </Button>
          </>
        }
      >
        {sichtbareEintraege.length > 0 && (
          <nav className="ksb-nav" aria-label="Hauptnavigation">
            {sichtbareEintraege.map((e) => (
              <NavLink key={e.ziel} to={e.ziel} className="ksb-nav__link">
                {e.text}
              </NavLink>
            ))}
          </nav>
        )}
      </HeaderBar>
      {/* Seiten werden nachgeladen (App.tsx); die Kopfzeile bleibt dabei stehen. */}
      {verwaltung ? (
        <Suspense fallback={<Ladeanzeige />}>
          <Outlet />
        </Suspense>
      ) : (
        <main className={styles.inhalt}>
          <Suspense fallback={<Ladeanzeige />}>
            <Outlet />
          </Suspense>
        </main>
      )}
    </>
  );
}
